import { type Editor, Extension } from "@tiptap/core";
import { Selection, TextSelection } from "@tiptap/pm/state";
import { addRow } from "./nodeviews/table-commands";

export function appendGroupItem(editor: Editor, groupPos: number): boolean {
  if (!editor.isEditable) return false;
  const group = editor.state.doc.nodeAt(groupPos);
  const item = editor.schema.nodes.klGroupItem?.createAndFill();
  if (!group || !item) return false;
  const at = groupPos + group.nodeSize - 1;
  const tr = editor.state.tr.insert(at, item);
  tr.setSelection(TextSelection.near(tr.doc.resolve(at + 1)));
  editor.view.dispatch(tr);
  editor.view.focus();
  return true;
}

export function insertGroupAfter(editor: Editor, groupPos: number): boolean {
  if (!editor.isEditable) return false;
  const group = editor.state.doc.nodeAt(groupPos);
  const next = editor.schema.nodes.klGroup?.createAndFill({ title: "", meta: null });
  if (!group || !next) return false;
  const at = groupPos + group.nodeSize;
  editor.view.dispatch(editor.state.tr.insert(at, next));
  return true;
}

export function removeGroup(editor: Editor, groupPos: number): boolean {
  if (!editor.isEditable) return false;
  const group = editor.state.doc.nodeAt(groupPos);
  const parent = editor.state.doc.resolve(groupPos).parent;
  if (!group || parent.childCount <= 1) return false;
  editor.view.dispatch(editor.state.tr.delete(groupPos, groupPos + group.nodeSize));
  return true;
}

/** Enter in a group item → new item; Enter in a kv value → new pair. */
export function splitEntry(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty) return false;
  if ($from.parent.type.name === "paragraph" && $from.depth >= 2 && $from.node(-1).type.name === "klGroupItem") {
    return editor.commands.command(({ tr }) => {
      tr.split($from.pos, 2);
      return true;
    });
  }
  if ($from.parent.type.name === "klKvPair") {
    const type = editor.schema.nodes.klKvPair;
    if (!type) return false;
    return editor.commands.command(({ tr }) => {
      tr.split($from.pos, 1, [{ type, attrs: { key: "" } }]);
      return true;
    });
  }
  return false;
}

/** C3: one code block holds one line. Enter starts the next one; Enter on an empty one returns to text. */
export function enterInCode(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  const name = $from.parent.type.name;
  if (!empty || (name !== "codeBlock" && name !== "klCodeLine")) return false;
  if ($from.parent.content.size === 0) return editor.commands.setNode("paragraph");
  return editor.commands.command(({ tr }) => {
    tr.split($from.pos);
    return true;
  });
}

/** Backspace at the start of an empty kv pair or group item removes it (when it is not the only one). */
export function removeEmptyEntry(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || $from.parentOffset !== 0 || $from.parent.content.size !== 0) return false;
  let from = -1;
  let size = 0;
  if ($from.parent.type.name === "klKvPair" && $from.node(-1).childCount > 1) {
    from = $from.before();
    size = $from.parent.nodeSize;
  } else if (
    $from.parent.type.name === "paragraph" &&
    $from.depth >= 3 &&
    $from.node(-1).type.name === "klGroupItem" &&
    $from.node(-1).childCount === 1 &&
    $from.node(-2).childCount > 1
  ) {
    from = $from.before(-1);
    size = $from.node(-1).nodeSize;
  }
  if (from < 0) return false;
  return editor.commands.command(({ tr }) => {
    tr.delete(from, from + size);
    tr.setSelection(Selection.near(tr.doc.resolve(Math.max(0, from - 1)), -1));
    return true;
  });
}

/** Tab / Shift-Tab between table cells; Tab in the last cell appends a row. */
export function moveCell(editor: Editor, dir: 1 | -1): boolean {
  const { state } = editor;
  const { $from } = state.selection;
  let d = $from.depth;
  while (d > 0 && $from.node(d).type.name !== "klTableCell") d--;
  if (d < 3) return false;
  const tablePos = $from.before(d - 2);
  const table = $from.node(d - 2);
  const cells: number[] = [];
  table.descendants((n, pos) => {
    if (n.type.name === "klTableCell") {
      cells.push(tablePos + 1 + pos);
      return false;
    }
    return true;
  });
  const target = cells[cells.indexOf($from.before(d)) + dir];
  if (target === undefined) {
    if (dir === -1) return true;
    const tr = addRow(state, tablePos, null);
    if (!tr) return true;
    editor.view.dispatch(tr);
    return moveCell(editor, 1);
  }
  const cell = state.doc.nodeAt(target);
  const end = target + (cell?.nodeSize ?? 2) - 1;
  editor.view.dispatch(state.tr.setSelection(TextSelection.near(state.doc.resolve(end), -1)));
  return true;
}

export const StructureKeymap = Extension.create({
  name: "klarityStructureKeys",
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => splitEntry(editor) || enterInCode(editor),
      Backspace: ({ editor }) => removeEmptyEntry(editor),
      Tab: ({ editor }) => moveCell(editor, 1),
      "Shift-Tab": ({ editor }) => moveCell(editor, -1),
    };
  },
});
